import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Award, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function Quiz() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    const c = await api.get(`/courses/${slug}`);
    setCourse(c.data);
    const q = await api.get(`/courses/${c.data.id}/quiz`);
    setQuiz(q.data);
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  if (!course || !quiz) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  function toggleAnswer(qid, idx, single) {
    setAnswers((prev) => {
      const cur = new Set(prev[qid] || []);
      if (single) return { ...prev, [qid]: [idx] };
      cur.has(idx) ? cur.delete(idx) : cur.add(idx);
      return { ...prev, [qid]: Array.from(cur) };
    });
  }

  async function submit() {
    const r = await api.post(`/courses/${course.id}/quiz/submit`, { answers });
    setResult(r.data);
    if (r.data.passed) toast.success(`Passed! Score ${r.data.score}%`);
    else toast.error(`Score ${r.data.score}% — passing is ${quiz.passing_percentage}%`);
  }

  if (result) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16" data-testid="quiz-result">
        <div className={`h-24 w-24 mx-auto rounded-full flex items-center justify-center ${result.passed ? "bg-emerald-500/15" : "bg-destructive/15"}`}>
          {result.passed ? <Award className="h-12 w-12 text-emerald-500" /> : <XCircle className="h-12 w-12 text-destructive" />}
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-6">
          {result.passed ? "Congratulations!" : "Almost there"}
        </h1>
        <p className="mt-3 text-muted-foreground">
          You scored <span className="font-bold text-foreground">{result.score}%</span> · {result.correct}/{result.total} correct.
        </p>
        {result.passed && <p className="mt-2 text-gold font-semibold">A certificate has been added to your profile 🎉</p>}
        <div className="mt-8 flex justify-center gap-3">
          <Button onClick={() => { setResult(null); setAnswers({}); }} variant="outline" className="rounded-full">Retake</Button>
          <Button onClick={() => nav(`/app/certificates`)} className="rounded-full royal-gradient border-0 text-white">View certificates</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="quiz-page">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-gold font-semibold">Final assessment</p>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">{quiz.title}</h1>
        <p className="text-muted-foreground mt-2">Passing score: {quiz.passing_percentage}%. Good luck!</p>
      </div>

      {quiz.questions.map((q, qi) => {
        const single = q.type !== "multi";
        return (
          <div key={q.id} className="rounded-3xl bg-card border border-border p-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Question {qi + 1} · {q.type}</p>
            <p className="font-display text-xl font-bold mt-2">{q.question}</p>
            <div className="mt-4 space-y-2">
              {q.options.map((opt, i) => {
                const selected = (answers[q.id] || []).includes(i);
                return (
                  <button key={`${q.id}-opt-${i}`}
                          data-testid={`quiz-opt-${q.id}-${i}`}
                          onClick={() => toggleAnswer(q.id, i, single)}
                          className={`w-full text-left px-4 py-3 rounded-2xl border transition flex items-center gap-3 ${
                            selected ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"
                          }`}>
                    <span className={`h-5 w-5 rounded-full border flex items-center justify-center ${selected ? "bg-primary border-primary text-primary-foreground" : "border-border"}`}>
                      {selected && <CheckCircle2 className="h-3.5 w-3.5" />}
                    </span>
                    <span className="text-sm">{opt}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      <Button onClick={submit} data-testid="quiz-submit"
        className="w-full h-12 rounded-full royal-gradient border-0 text-white btn-lift">
        Submit quiz
      </Button>
    </div>
  );
}
